/**
 * Normalize browse subcategory `keywords` from config (string or string[]) for matching and URLs.
 * URL / discovery: pipe-separated patterns are OR'd; a single string without `|` is one phrase
 * (all whitespace-separated tokens must appear in the haystack as whole words).
 */

const escapeRegExp = s => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whole-word token regexes are reused across ~73k listings × ~1k keywords during browse menu rebuild. */
const tokenWordRegexCache = new Map();

const tokenWordRegex = token => {
  let cached = tokenWordRegexCache.get(token);
  if (!cached) {
    cached = new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(token)}(?:[^a-z0-9]|$)`);
    tokenWordRegexCache.set(token, cached);
  }
  return cached;
};

/** Match a token as a whole word — not as a substring inside another word (e.g. "serie" in "series"). */
export const tokenMatchesHaystack = (token, haystack) => {
  const t = String(token || '').toLowerCase().trim();
  if (!t) {
    return false;
  }
  const text = String(haystack || '').toLowerCase();
  if (/^[a-z0-9][a-z0-9-]*$/i.test(t)) {
    return tokenWordRegex(t).test(text);
  }
  return text.includes(t);
};

export const patternMatchesHaystack = (pattern, haystack) => {
  const tokens = String(pattern || '')
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!tokens.length) {
    return false;
  }
  return tokens.every(t => tokenMatchesHaystack(t, haystack));
};

export const keywordPatternsFromConfig = keywords => {
  if (keywords == null) {
    return [];
  }
  if (Array.isArray(keywords)) {
    return keywords
      .map(k => String(k).toLowerCase().trim())
      .filter(Boolean);
  }
  if (typeof keywords !== 'string') {
    return [];
  }
  const trimmed = keywords.trim();
  if (!trimmed) {
    return [];
  }
  if (trimmed.includes('|')) {
    return trimmed
      .split('|')
      .map(k => k.toLowerCase().trim())
      .filter(Boolean);
  }
  return [trimmed.toLowerCase()];
};

/** First pattern for a short search URL when many synonyms are configured. */
export const primaryKeywordForSearchUrl = keywords => {
  const patterns = keywordPatternsFromConfig(keywords);
  return patterns.length ? patterns[0] : null;
};

/** Pipe-separated value for discovery `keywords` query param (OR match). */
export const keywordPatternsToSearchParam = keywords => {
  const patterns = keywordPatternsFromConfig(keywords);
  return patterns.length ? patterns.join('|') : null;
};

export const haystackMatchesKeywordPatterns = (haystack, keywords) => {
  const patterns = keywordPatternsFromConfig(keywords);
  if (!patterns.length) {
    return true;
  }
  return patterns.some(pattern => {
    if (!pattern) {
      return false;
    }
    if (pattern.includes('|')) {
      return pattern
        .split('|')
        .some(p => p.trim() && patternMatchesHaystack(p.trim(), haystack));
    }
    return patternMatchesHaystack(pattern, haystack);
  });
};

const patternsFromBrowseRule = rule => {
  const patterns = keywordPatternsFromConfig(rule?.keywords);
  if (Array.isArray(rule?.matchAny)) {
    for (const item of rule.matchAny) {
      const p = String(item).toLowerCase().trim();
      if (p && !patterns.includes(p)) {
        patterns.push(p);
      }
    }
  }
  return patterns;
};

const SPORTS_ENUM_OPTION = 'sport-fitness';
export const SPORTS_MATCHUPS_SLUG = 'sports-matchups';

/** Two-team sports titles: "Lakers vs Celtics", "Yankees vs. Red Sox", etc. */
export const isSportsMatchupTitle = haystack => /\bvs\.?\b/i.test(String(haystack || ''));

export const isSportsBrowseRule = rule =>
  rule?.enumOption === SPORTS_ENUM_OPTION || String(rule?.slug || '').startsWith('sports-');

/** @see server/lib/browseCategoryKeywords.js — keep in sync */
const NON_SPORT_ENTERTAINMENT_TITLE = [
  /-\s*band\b/i,
  /\b(the musical| - the musical|- the musical)\b/i,
  /\b(musical parody|stage musical|musical theatre|musical theater)\b/i,
  /\b(broadway|off-broadway|west end)\b/i,
  /\b(theatre|theater)\b/i,
  /\b(opera|symphony|philharmonic|orchestra)\b/i,
  /\bconcert\b/i,
  /\b(hunchback of notre dame|notre dame de paris)\b/i,
  /\bstand-up comedy\b|\bstand up comedy\b|\bcomedy tour\b/i,
];

export const haystackBlocksSportsBrowseKeywordMatch = haystack => {
  const text = String(haystack || '');
  if (!text) {
    return false;
  }
  return NON_SPORT_ENTERTAINMENT_TITLE.some(re => re.test(text));
};

export const haystackMatchesBrowseRuleKeywordsOnly = (haystack, rule) => {
  const patterns = patternsFromBrowseRule(rule);
  if (!patterns.length) {
    return false;
  }
  return patterns.some(pattern => patternMatchesHaystack(pattern, haystack));
};

export const haystackMatchesBrowseRule = (haystack, rule, options = {}) => {
  const text = String(haystack || '');

  if (isSportsBrowseRule(rule) && haystackBlocksSportsBrowseKeywordMatch(text)) {
    return false;
  }

  if (isSportsMatchupTitle(text)) {
    if (!isSportsBrowseRule(rule)) {
      return false;
    }
    if (rule?.slug === SPORTS_MATCHUPS_SLUG) {
      const siblings = options.siblingSportsRules;
      if (Array.isArray(siblings) && siblings.length > 0) {
        const hasSpecificSport = siblings.some(
          r =>
            r?.slug !== SPORTS_MATCHUPS_SLUG &&
            haystackMatchesBrowseRuleKeywordsOnly(text, r)
        );
        if (hasSpecificSport) {
          return false;
        }
      }
      return true;
    }
    return haystackMatchesBrowseRuleKeywordsOnly(text, rule);
  }

  return haystackMatchesBrowseRuleKeywordsOnly(text, rule);
};
